import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cn } from "~/lib/cn";

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optionalLabel?: string;
  className?: string;
  children: ReactElement<{ id?: string; "aria-invalid"?: boolean; "aria-describedby"?: string }>;
}

/** Label, control, hint and error wired together for assistive technologies. */
export function Field({ label, hint, error, optionalLabel, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": [hintId, errorId].filter(Boolean).join(" ") || undefined,
      })
    : children;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="flex items-baseline justify-between gap-2 text-[13px] font-medium text-fg">
        <span>{label}</span>
        {optionalLabel ? <span className="text-xs font-normal text-fg-3">{optionalLabel}</span> : null}
      </label>
      {control}
      {hint && !error ? (
        <p id={hintId} className="text-xs text-fg-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function FieldGroup({ title, children, className }: { title?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <fieldset className={cn("flex flex-col gap-4", className)}>
      {title ? <legend className="mb-1 font-display text-[15px] font-medium text-fg">{title}</legend> : null}
      {children}
    </fieldset>
  );
}
